import { z } from 'zod/v3';
import {
  PopulationTypeSchema,
  PopulationSourceTypeSchema,
  SampleItemStatusSchema,
  SamplingMethodSchema,
} from '../generated/enums';

// Re-export types from generated enums for convenience
export type { PopulationType, PopulationSourceType, SampleItemStatus, SamplingMethod } from '../generated/enums';

// ===== Column Mapping Types =====

/** Maps a required field name to the actual column name in the uploaded file */
export const ColumnMappingSchema = z.record(z.string(), z.string());
export type ColumnMapping = z.infer<typeof ColumnMappingSchema>;

/** AI-suggested column mapping with confidence score */
export const ColumnMappingSuggestionSchema = z.object({
  requiredField: z.string(),
  suggestedColumn: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  reason: z.string().optional(),
});
export type ColumnMappingSuggestion = z.infer<typeof ColumnMappingSuggestionSchema>;

// ===== Population DTOs =====

export const PopulationDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  type: PopulationTypeSchema,
  sourceType: PopulationSourceTypeSchema,
  fileUrl: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  fileHash: z.string().nullable(),
  recordCount: z.number(),
  headers: z.array(z.string()),
  columnMappings: ColumnMappingSchema.nullable(),
  asOfDate: z.date(),
  periodStart: z.date().nullable(),
  periodEnd: z.date().nullable(),
  uploadedBy: z.string(),
  uploadedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type PopulationDTO = z.infer<typeof PopulationDTOSchema>;

export const CreatePopulationDTOSchema = z.object({
  name: z.string().min(1).max(200),
  type: PopulationTypeSchema,
  fileUrl: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  fileHash: z.string().optional(),
  // Allow 0 or omit - server will parse file to get actual count
  recordCount: z.number().min(0).optional(),
  headers: z.array(z.string()).optional(),
  asOfDate: z.date(),
  periodStart: z.date().optional(),
  periodEnd: z.date().optional(),
});

export type CreatePopulationDTO = z.infer<typeof CreatePopulationDTOSchema>;

// ===== Sample (SampleSet) DTOs =====

export const SampleDTOSchema = z.object({
  id: z.string(),
  populationId: z.string(),
  templateId: z.string().nullable(),

  // Selection method (auditor-controlled)
  method: SamplingMethodSchema,
  sampleSize: z.number(),
  seed: z.number().nullable(),
  selectedIndices: z.array(z.number()),
  stratifyBy: z.string().nullable(),

  // Audit trail
  selectedBy: z.string(),
  selectedAt: z.date(),
  notes: z.string().nullable(),

  // Results
  passedCount: z.number().nullable(),
  failedCount: z.number().nullable(),
  pendingCount: z.number().nullable(),

  // Shared evidence results
  sharedEvidenceResults: z.unknown().nullable(),

  createdAt: z.date(),
  updatedAt: z.date(),

  // Optional nested objects for display
  population: PopulationDTOSchema.optional(),
  evidenceStatusId: z.string().nullable().optional(),
});

export type SampleDTO = z.infer<typeof SampleDTOSchema>;

export const CreateSampleDTOSchema = z.object({
  populationId: z.string(),

  // Selection method (auditor-controlled, NOT template-prescribed)
  method: SamplingMethodSchema.default('RANDOM'),
  sampleSize: z.number().min(1),
  seed: z.number().optional(),
  stratifyBy: z.string().optional(),

  // Selection details
  notes: z.string().optional(),
  /** Item indices to include (for judgmental/manual selection) */
  selectedIndices: z.array(z.number()).optional(),

  // Template reference (for evidence specification only)
  templateId: z.string().optional(),
  /** Control IDs to associate with the evidence status */
  controlIds: z.array(z.string()).optional(),
  /** Link sample to a ControlEvidenceStatus (test) */
  statusId: z.string().optional(),

  // RFC-107: Identifier field selection
  /** Which column to use as the sample item identifier (defaults to first column) */
  identifierField: z.string().optional(),
});

export type CreateSampleDTO = z.infer<typeof CreateSampleDTOSchema>;

// ===== Sample Item DTOs =====

export const SampleItemDTOSchema = z.object({
  id: z.string(),
  sampleId: z.string(),
  rowIndex: z.number(),
  identifier: z.string(),
  rowData: z.record(z.unknown()),
  status: SampleItemStatusSchema,
  evidenceUrl: z.string().nullable(),
  evidenceNotes: z.string().nullable(),
  passed: z.boolean().nullable(),
  failureReason: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.date().nullable(),

  // Replacement tracking
  isReplacement: z.boolean(),
  replacesSampleItemId: z.string().nullable(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export type SampleItemDTO = z.infer<typeof SampleItemDTOSchema>;

export const UpdateSampleItemDTOSchema = z.object({
  id: z.string(),
  status: SampleItemStatusSchema.optional(),
  evidenceUrl: z.string().nullable().optional(),
  evidenceNotes: z.string().nullable().optional(),
  passed: z.boolean().nullable().optional(),
  failureReason: z.string().nullable().optional(),
});

export type UpdateSampleItemDTO = z.infer<typeof UpdateSampleItemDTOSchema>;

// ===== Template Sampling Config (Evidence Specification) =====
// Note: This defines WHAT evidence is needed, NOT HOW to sample (auditor decision)

/** Validator types for shared evidence matching */
export const SharedEvidenceValidatorTypeSchema = z.enum([
  'csv_contains',      // CSV column contains sampled identifiers
  'text_contains',     // Text/OCR contains sampled identifiers
  'timestamp_within_period', // Evidence timestamp within audit period
  'custom',            // Custom validation rule
]);

export type SharedEvidenceValidatorType = z.infer<typeof SharedEvidenceValidatorTypeSchema>;

/** Shared evidence validator configuration */
export const SharedEvidenceValidatorConfigSchema = z.object({
  type: SharedEvidenceValidatorTypeSchema,
  field: z.string().optional(),  // For csv_contains: which column to check
  pattern: z.string().optional(), // For text_contains: regex pattern
  description: z.string().optional(),
});

export type SharedEvidenceValidatorConfig = z.infer<typeof SharedEvidenceValidatorConfigSchema>;

/** Item checklist element types */
export const ItemChecklistElementTypeSchema = z.enum([
  'screenshot',
  'file_upload',
  'text_input',
  'confirmation',
]);

export type ItemChecklistElementType = z.infer<typeof ItemChecklistElementTypeSchema>;

/** Per-item checklist item configuration */
export const ItemChecklistConfigSchema = z.object({
  type: ItemChecklistElementTypeSchema,
  label: z.string(),  // Supports {{row.field}} placeholders
  required: z.boolean().default(true),
  validators: z.array(z.union([
    z.string(),  // Simple validator name
    z.object({   // Validator with params
      type: z.string(),
      params: z.record(z.unknown()).optional(),
    }),
  ])).optional(),
});

export type ItemChecklistConfig = z.infer<typeof ItemChecklistConfigSchema>;

/** Period rules for evidence collection */
export const PeriodRulesConfigSchema = z.object({
  mode: z.enum(['point_in_time', 'window']),
  windowDays: z.number().optional(),  // For window mode: how many days
});

export type PeriodRulesConfig = z.infer<typeof PeriodRulesConfigSchema>;

/** Template sampling configuration (evidence specification) */
export const TemplateSamplingConfigSchema = z.object({
  // Population requirements
  requiresPopulation: z.boolean(),
  populationType: PopulationTypeSchema.optional(),
  populationFields: z.array(z.string()).optional(), // Required columns for binding

  // Recommendations (hints only, NOT enforced)
  minimumSampleSizeRecommended: z.number().optional(),
  recommendedMethod: SamplingMethodSchema.optional(),

  // Evidence collection mode
  sharedEvidenceSupported: z.boolean().default(false),
  sharedEvidenceValidators: z.array(SharedEvidenceValidatorConfigSchema).optional(),

  // Per-item requirements (when not using shared evidence)
  itemChecklist: z.array(ItemChecklistConfigSchema).optional(),

  // Period rules
  periodRules: PeriodRulesConfigSchema.optional(),

  // Dynamic parameter binding (e.g., { employeeEmail: '{{row.email}}' })
  dynamicParams: z.record(z.string()).optional(),
});

export type TemplateSamplingConfig = z.infer<typeof TemplateSamplingConfigSchema>;

// ===== Shared Evidence Validator Results =====

export const ValidatorResultSchema = z.object({
  type: SharedEvidenceValidatorTypeSchema,
  field: z.string().optional(),
  result: z.enum(['pass', 'partial_pass', 'fail']),
  coverage: z.object({
    matchedCount: z.number(),
    totalCount: z.number(),
    missing: z.array(z.string()).optional(),
  }).optional(),
  message: z.string().optional(),
});

export type ValidatorResult = z.infer<typeof ValidatorResultSchema>;

export const SharedEvidenceResultsSchema = z.object({
  attachments: z.array(z.object({
    id: z.string(),
    kind: z.enum(['file', 'url']),
    name: z.string(),
    hash: z.string().optional(),
    uploadedAt: z.date(),
  })),
  validatorsRun: z.array(ValidatorResultSchema),
});

export type SharedEvidenceResults = z.infer<typeof SharedEvidenceResultsSchema>;
